import React from "react";
import { Descendant } from "slate";
import { Slate, Editable } from "slate-react";
import { ManualEditorProps } from "components/ManualBuilder/types";
import { Toolbar } from "components/ManualBuilder/components/toolbar";
import { useEditorConfig } from "./hooks/useEditorConfig";
import { useKeyboardHandlers } from "./hooks/useKeyboardHandlers";
import { useEditorRenderers } from "./hooks/useEditorRenderers";

const initialValue: Descendant[] = [
  {
    type: "paragraph",
    children: [{ text: "Start typing here..." }],
  },
];

export const ManualEditor = (props: ManualEditorProps) => {
  const editor = useEditorConfig();
  const onKeyDown = useKeyboardHandlers(editor);
  const { renderElement, renderLeaf } = useEditorRenderers();
  const [value, setValue] = React.useState<Descendant[]>(
    props.defaultValue || initialValue,
  );
  const initialValueRef = React.useRef<Descendant[]>(
    props.defaultValue || initialValue,
  );
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Update the initial value ref when defaultValue changes (new chapter selected)
  React.useEffect(() => {
    initialValueRef.current = props.defaultValue || initialValue;
  }, [props.defaultValue]);

  if (!props.defaultValue?.length) {
    return <>Wähle zunächst ein Kapitel aus der Liste</>;
  }

  const handleBlur = () => {
    props.onBlur?.(value);
  };

  const handleChange = (content: Descendant[]) => {
    setValue(content);
    // Check if content has changed from initial value
    const hasChanged =
      JSON.stringify(content) !== JSON.stringify(initialValueRef.current);
    props.onChange?.(content, hasChanged);

    // Trigger debounced save on change (200ms)
    if (hasChanged) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        props.onBlur?.(content);
      }, 200);
    }
  };

  return (
    <div>
      <div
        className="bg-white rounded-md shadow relative overflow-hidden"
        style={{ height: "75vh" }}
      >
        <Slate
          editor={editor}
          onChange={handleChange}
          initialValue={props.defaultValue || initialValue}
        >
          <div className="sticky top-0 z-10 bg-white border-b">
            <Toolbar />
          </div>
          <div className="p-4 overflow-y-auto h-[calc(100%-48px)]">
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder="Start typing..."
              className="min-h-[200px] focus:outline-none"
              onKeyDown={onKeyDown}
              onBlur={handleBlur}
            />
          </div>
        </Slate>
      </div>
    </div>
  );
};
